/**
 * Personificar Instalação — estado global da sessão de personificação.
 *
 * Um singleton com pub/sub simples (não um Context React): `http.ts` precisa
 * ler o valor atual de forma SÍNCRONA a cada requisição (pra montar o header
 * `X-Personificar-Instalacao`), fora de qualquer árvore de componentes.
 * `sessionStorage` faz a personificação sobreviver a F5/navegação dentro da
 * mesma aba, mas nunca a uma aba nova — é uma sessão de suporte, não algo pra
 * persistir indefinidamente.
 */

export interface Personificacao {
  instalacaoId: string;
  nome: string;
  /** Id de `pan.instalacoes_infra` — usado só pra gerenciar a trava de sessão
   * (heartbeat/liberação) e a auditoria, nunca vai no header. `null` quando a
   * instalação não tinha esse id na busca (ex.: modo demo). */
  instalacaoInfraId: number | null;
}

const CHAVE_STORAGE = 'pan.personificacao';
export const HEADER_PERSONIFICACAO = 'X-Personificar-Instalacao';

function carregarDoStorage(): Personificacao | null {
  try {
    const bruto = sessionStorage.getItem(CHAVE_STORAGE);
    if (!bruto) return null;
    const valor = JSON.parse(bruto) as Partial<Personificacao>;
    if (typeof valor.instalacaoId === 'string' && typeof valor.nome === 'string') {
      return {
        instalacaoId: valor.instalacaoId,
        nome: valor.nome,
        instalacaoInfraId: typeof valor.instalacaoInfraId === 'number' ? valor.instalacaoInfraId : null,
      };
    }
    return null;
  } catch {
    return null;
  }
}

function salvarNoStorage(valor: Personificacao | null): void {
  try {
    if (valor) sessionStorage.setItem(CHAVE_STORAGE, JSON.stringify(valor));
    else sessionStorage.removeItem(CHAVE_STORAGE);
  } catch {
    // Storage bloqueado (aba anônima com política restrita, etc.) — a
    // personificação simplesmente não sobrevive a um F5, sem quebrar o resto.
  }
}

let atual: Personificacao | null = carregarDoStorage();
const ouvintes = new Set<() => void>();

export function obterPersonificacao(): Personificacao | null {
  return atual;
}

export function definirPersonificacao(valor: Personificacao | null): void {
  atual = valor;
  salvarNoStorage(valor);
  ouvintes.forEach((ouvinte) => ouvinte());
}

export function assinarPersonificacao(ouvinte: () => void): () => void {
  ouvintes.add(ouvinte);
  return () => ouvintes.delete(ouvinte);
}
